import {
  ApiResponse,
  IActiveSubscription,
  IDowngradePreviewResponse,
  IExecuteDowngradeResponse,
  IPlan,
} from '@/types';

import http from '../http';

export const getPlanList = async () =>
  http.get<ApiResponse<IPlan[]>>('/plans?includeStripeData=true');

export const getPublicPlanList = async () =>
  http.get<ApiResponse<IPlan[]>>('/plans?includeStripeData=false&publicCatalog=true');

export const getActiveSubscription = async () =>
  http.get<ApiResponse<{ subscription: IActiveSubscription }>>('/subscription/me/active');

// Pulls the merchant's current subscription from Shopify and syncs the plan.
// Recovery for missed app_subscriptions/update webhooks (endpoint returns a
// bare { synced } payload, not the ApiResponse envelope).
export const syncShopifyBilling = async () =>
  http.post<{ synced: number }>('/integrations/shopify/billing/sync', {});

export const getSubscriptionForDisplay = async () =>
  http.get<ApiResponse<{ subscription: IActiveSubscription }>>('/subscription/me/display');

export const checkReactivationEligibility = async (planId: string) =>
  http.get<ApiResponse<{ eligible: boolean; subscription: any }>>(
    `/subscription/check-reactivation/${planId}`
  );

export const reactivateSubscription = async (subscriptionId: string) =>
  http.post<ApiResponse<{ success: boolean; subscription: any }>>(
    `/subscription/reactivate/${subscriptionId}`,
    {}
  );

export const cancelSubscription = async (subscriptionId: string, cancelReason?: string) =>
  http.post<ApiResponse<any>>(`/subscription/${subscriptionId}/cancel`, { cancelReason });

export const cancelAddon = async (addonId: string, quantity?: number) =>
  http.post<
    ApiResponse<{
      success: boolean;
      canceledQuantity: number;
      remainingQuantity: number;
    }>
  >(`/subscription/addon/${addonId}/cancel`, { quantity });

// Phase 8: Frozen Resources API
export interface FrozenResource {
  id: string;
  name?: string;
  fullDomain?: string;
  frozenAt: string;
  frozenReason: any;
  deletionDate: string;
  daysUntilDeletion: number;
}

export interface FrozenResourcesResponse {
  totalCount: number;
  urgency: 'none' | 'warning' | 'critical';
  deletionWindowDays: number;
  resources: {
    chatbots: FrozenResource[];
    domains: FrozenResource[];
  };
}

export const getFrozenResources = async () =>
  http.get<ApiResponse<FrozenResourcesResponse>>('/subscription/me/frozen-resources');

// Phase 8: Payment Status API
export interface PaymentStatusResponse {
  hasIssue: boolean;
  status: string;
  subscriptionStatus?: string;
  lastFailure?: string | null;
  retryCount?: number;
  maxRetries?: number;
  nextRetry?: string | null;
  suspensionDate?: string | null;
  isSuspended?: boolean;
  updatePaymentUrl?: string;
}

export const getPaymentStatus = async () =>
  http.get<ApiResponse<PaymentStatusResponse>>('/subscription/me/payment-status');

// Usage alert settings (message credit warnings + auto top-up)
export interface UsageAlertSettings {
  id: string;
  ownerUserId: string;
  extraEmails: string[];
  emailAt80: boolean;
  emailAt90: boolean;
  emailAt100: boolean;
  autoTopUpEnabled: boolean;
  autoTopUpTriggerRemaining: number;
  autoTopUpCapPerPeriod: number;
}

export const getUsageAlertSettings = async () =>
  http.get<ApiResponse<{ settings: UsageAlertSettings }>>('/subscription/usage-alert-settings');

export const updateUsageAlertSettings = async (payload: Partial<UsageAlertSettings>) =>
  http.patch<ApiResponse<{ settings: UsageAlertSettings }>>(
    '/subscription/usage-alert-settings',
    payload
  );

// NEW: Plan Change API (Preserves Add-ons)
export interface FeatureChange {
  featureName: string;
  currentValue: number;
  newValue: number;
  change: number; // Positive = increase, negative = decrease
  changeType: 'increase' | 'decrease' | 'unchanged';
  featureModel: 'consumption' | 'allocation' | 'other';
  isUnlimited?: boolean;
  description: string;
}

export interface ResourceToFreeze {
  resourceType: 'chatbot' | 'team_seat' | 'knowledge_base' | 'custom_domain';
  resourceName: string;
  resourceId: string;
  reason: string;
}

export interface PlanChangePreviewResponse {
  currentPlan: {
    id: string;
    name: string;
    price: number;
  };
  newPlan: {
    id: string;
    name: string;
    price: number;
  };
  isUpgrade: boolean;
  /**
   * What THIS plan change costs — the sum of Stripe's proration lines only.
   * Positive = charge, negative = credit. Not the upcoming invoice total.
   */
  prorationAmount: number;
  currency: string;
  effectiveDate: string;
  nextInvoiceDate: string;
  description: string;
  /** Subscription is still in its trial. */
  isTrialing?: boolean;
  /**
   * True only when money actually moves today. `create_prorations` (an
   * immediate paid -> paid change) attaches the proration to the NEXT invoice,
   * so this is false and `prorationAmount` is not a charge due today.
   */
  collectsToday?: boolean;
  /** Change lands now vs at period end. */
  appliesImmediately?: boolean;
  // Enhanced feature preview
  featureChanges: FeatureChange[];
  resourcesToFreeze: ResourceToFreeze[];
  warnings: string[];
}

export interface PlanChangeResponse {
  success: boolean;
  subscription: any;
  prorationAmount?: number;
  effectiveDate: string;
  featureSync: any;
}

export const previewPlanChange = async (data: {
  subscriptionId: string;
  newPlanId: string;
  billingCycle?: 'monthly' | 'yearly';
}) => http.post<ApiResponse<PlanChangePreviewResponse>>('/subscription/change-plan/preview', data);

export const changePlan = async (data: {
  subscriptionId: string;
  newPlanId: string;
  immediate?: boolean;
  billingCycle?: 'monthly' | 'yearly';
}) => http.post<ApiResponse<PlanChangeResponse>>('/subscription/change-plan', data);

// Downgrade with chatbot selection
export const previewDowngrade = async (data: {
  subscriptionId: string;
  newPlanId: string;
  billingCycle?: 'monthly' | 'yearly';
}) =>
  http.post<ApiResponse<IDowngradePreviewResponse>>(
    '/subscription/change-plan/preview-downgrade',
    data
  );

export const executeDowngrade = async (data: {
  subscriptionId: string;
  newPlanId: string;
  chatbotsToFreeze: string[];
  immediate?: boolean;
  billingCycle?: 'monthly' | 'yearly';
}) =>
  http.post<ApiResponse<IExecuteDowngradeResponse>>(
    '/subscription/change-plan/execute-downgrade',
    data
  );

// Upgrade with chatbot unfreeze selection
export interface FrozenChatbotInfo {
  id: string;
  name: string;
  frozenAt: string;
  frozenReason: string;
  daysUntilDeletion: number;
  deletionDate: string;
  canBeUnfrozen: boolean;
  createdFromAddonId: string | null;
}

export interface IUpgradePreviewResponse {
  currentPlan: {
    id: string;
    name: string;
    chatbotLimit: number;
    isUnlimited: boolean;
  };
  newPlan: {
    id: string;
    name: string;
    chatbotLimit: number;
    isUnlimited: boolean;
  };
  activeChatbotsCount: number;
  gainedChatbotSlots: number;
  hasFrozenChatbots: boolean;
  canUnfreezeChatbots: boolean;
  frozenChatbots: FrozenChatbotInfo[];
  maxUnfreezeCount: number;
  proration: {
    amount: number;
    currency: string;
    effectiveDate: string;
  };
}

export interface IExecuteUpgradeResponse {
  success: boolean;
  subscription: {
    id: string;
    planId: string;
    status: string;
  };
  unfrozenChatbots: Array<{
    id: string;
    name: string;
  }>;
  stillFrozenChatbots: Array<{
    id: string;
    name: string;
  }>;
  effectiveDate: string;
}

export const previewUpgrade = async (data: {
  subscriptionId: string;
  newPlanId: string;
  billingCycle?: 'monthly' | 'yearly';
}) =>
  http.post<ApiResponse<IUpgradePreviewResponse>>(
    '/subscription/change-plan/preview-upgrade',
    data
  );

export const executeUpgrade = async (data: {
  subscriptionId: string;
  newPlanId: string;
  chatbotsToUnfreeze: string[];
  immediate?: boolean;
  billingCycle?: 'monthly' | 'yearly';
}) =>
  http.post<ApiResponse<IExecuteUpgradeResponse>>(
    '/subscription/change-plan/execute-upgrade',
    data
  );

export interface IUnfreezeChatbotResponse {
  success: boolean;
  chatbot: {
    id: string;
    name: string;
  };
}

export const unfreezeChatbot = async (chatbotId: string) =>
  http.post<ApiResponse<IUnfreezeChatbotResponse>>(`/subscription/unfreeze-chatbot/${chatbotId}`);
